// HOT POTATO (§4.2): Bonk Royale's one life and win check, plus a googly-eyed ticking bomb.
//  - Assignment: at tick 120, and 120 ticks after each explosion while 2+ beans live, the bomb
//    attaches to a random living bean; with more than 2 alive, never to the lowest match score
//    (ties excluded together, as long as 2 candidates remain).
//  - Fuse: secret, (8 + 1.2 × alive) s ± 2 s. Clients only get `potatoIntensity` 0–15, which ramps
//    over the public nominal fuse (8 + 1.2 × alive) s, so it reveals nothing the formula doesn't.
//  - Passing: body contact (centers within r1 + r2 + 0.15), or the carrier's shove or grab
//    connecting. The giver is immune to a pass-back for 36 ticks; the carrier runs 12% faster.
//  - Explosion: the carrier is blasted up and away (vz 12, 10 m/s in a seeded direction) and KO'd
//    60 ticks later wherever it is (cause POTATO, credited to the last passer within 300 ticks);
//    beans within 3.5 m take lerp(14, 7) · dizzyMult and +25 Dizzy, credited the same way.
// Everything here is server-authoritative; predict mode only reads `potatoSlot` for the carrier's
// speed (§18.2). ModeState use: `potatoFuse` = secret ticks left, `potatoFuseMax` = nominal fuse in
// ticks, `potatoNextTick` = the next assignment tick, or the assignment tick while a bomb is live.
import { NO_SLOT, TICK_HZ, TUNE } from '../constants';
import { EV, emit } from '../events';
import { BS, Cause, ModeId } from '../ids';
import type { RoundClock } from '../maps/clock';
import { TAU } from '../math';
import { rand, randInt, randRange } from '../rng';
import { applyBlastAt, applyKnockback, canBeHit } from '../sim/combat';
import { commitKO } from '../sim/edges';
import { aliveCount, setTouch } from '../sim/world';
import type { Bean, World } from '../types';
import type { ModeRules } from './modeTypes';
import { ROYALE, oneLifeResults, royaleTick } from './royale';
import { dcos, dsin } from '../sim/dmath';

const P = TUNE.potato;

/** A blasted carrier waiting for its KO (server-only bookkeeping beside the world). */
const blasts = new WeakMap<World, { slot: number; koTick: number }>();

/** Beans that can hold or receive the bomb: in the round with a body on (or over) the arena. */
function canHold(b: Bean): boolean {
  if (!b.active || b.out) return false;
  switch (b.state) {
    case BS.FALLING: case BS.GHOST: case BS.RESPAWN: case BS.FROZEN:
      return false;
    default:
      return true;
  }
}

/** The bomb's carrier, or NO_SLOT. */
export function potatoCarrier(w: World): number {
  return w.mode.potatoSlot;
}

/**
 * Slots the next bomb may go to (§4.2): living beans; with more than 2 alive, the lowest match
 * score is excluded (ties together) as long as 2 candidates remain. Ascending slot order.
 */
export function potatoCandidates(w: World): number[] {
  const all = w.beans.filter(canHold).map((b) => b.slot);
  if (aliveCount(w) <= P.excludeLowestAboveAlive) return all;
  const score = (s: number): number => w.cfg.matchScores[s] ?? 0;
  const low = Math.min(...all.map(score));
  const keep = all.filter((s) => score(s) !== low);
  return keep.length >= P.minCandidates ? keep : all;
}

/** The public nominal fuse in ticks for `alive` living beans: (8 + 1.2 × alive) s. */
export function nominalFuseTicks(alive: number): number {
  return Math.round((P.fuseBaseSec + P.fusePerBeanSec * alive) * TICK_HZ);
}

/** The 0–15 intensity clients see: the elapsed share of the nominal fuse (never the secret fuse). */
export function potatoIntensity(elapsedTicks: number, nominalTicks: number): number {
  if (nominalTicks <= 0) return P.intensityMax;
  return Math.max(0, Math.min(P.intensityMax, Math.floor(((P.intensityMax + 1) * elapsedTicks) / nominalTicks)));
}

function assign(w: World): void {
  const cands = potatoCandidates(w);
  if (cands.length === 0) return;
  const m = w.mode;
  const slot = cands[randInt(w, cands.length)];
  const nominal = nominalFuseTicks(aliveCount(w));
  m.potatoSlot = slot;
  m.potatoFuseMax = nominal;
  m.potatoFuse = Math.max(1, Math.round(nominal + randRange(w, -P.fuseJitterSec, P.fuseJitterSec) * TICK_HZ));
  m.potatoNextTick = w.tick;
  m.potatoPasser = NO_SLOT;
  m.potatoPassTick = 0;
  m.potatoIntensity = 0;
  const b = w.beans[slot];
  b.hasPotato = true;
  emit(w, EV.POTATO_NEW, slot, NO_SLOT, 0, 0, b.pos.x, b.pos.y);
}

/** Hands the bomb from carrier `from` to `to` if `to` can take it (not immune, not a second pass this tick). */
function pass(w: World, from: number, to: number): boolean {
  const m = w.mode;
  if (from === to || m.potatoSlot !== from) return false;
  if (m.potatoPasser !== NO_SLOT && m.potatoPassTick === w.tick) return false;
  const r = w.beans[to];
  if (!r || !canHold(r) || r.potatoImmuneTicks > 0) return false;
  const f = w.beans[from];
  f.hasPotato = false;
  f.potatoImmuneTicks = P.passImmuneTicks;
  r.hasPotato = true;
  m.potatoSlot = to;
  m.potatoPasser = from;
  m.potatoPassTick = w.tick;
  emit(w, EV.POTATO_PASS, from, to, 0, 0, r.pos.x, r.pos.y);
  return true;
}

function explode(w: World): void {
  const m = w.mode;
  const c = w.beans[m.potatoSlot];
  const credit = m.potatoPasser !== NO_SLOT && w.tick - m.potatoPassTick <= P.passCreditTicks ? m.potatoPasser : NO_SLOT;
  const x = c.pos.x;
  const y = c.pos.y;
  const z = c.z;
  emit(w, EV.POTATO_BOOM, c.slot, credit, 0, 0, x, y);
  const a = rand(w) * TAU;
  const ux = dcos(a);
  const uy = dsin(a);
  // Knock the carrier off its feet first (drops what it carries, frees it from a grab, pops a balloon).
  if (canBeHit(c)) applyKnockback(w, c.slot, ux, uy, P.launchKb, { dizzy: 0, cause: Cause.POTATO, by: credit, scaleByDizzy: false, vz: P.launchVz, noTumble: true });
  c.hasPotato = false;
  c.state = BS.FALLING;
  c.st = 0;
  c.vel.x = ux * P.launchKb;
  c.vel.y = uy * P.launchKb;
  c.vz = P.launchVz;
  if (credit !== NO_SLOT) {
    setTouch(w, c.slot, credit, Cause.POTATO);
  } else {
    c.touchBy = NO_SLOT;
    c.touchCause = Cause.POTATO;
  }
  m.potatoSlot = NO_SLOT;
  m.potatoFuse = 0;
  m.potatoIntensity = 0;
  m.potatoNextTick = w.tick + P.nextTicks;
  blasts.set(w, { slot: c.slot, koTick: w.tick + P.koDelayTicks });
  applyBlastAt(w, x, y, z, P.blastRadius, P.kbCenter, P.kbEdge, P.dizzy, Cause.POTATO, credit, TUNE.bomb.maxDz, NO_SLOT);
}

/** Server: pending blast KOs, grab passes, assignment, the fuse and the explosion. */
function potatoTick(w: World): void {
  const m = w.mode;
  const pending = blasts.get(w);
  if (pending && w.tick >= pending.koTick) {
    blasts.delete(w);
    commitKO(w, pending.slot);
  }
  if (m.potatoSlot !== NO_SLOT) {
    for (const e of w.events) if (e.type === EV.GRAB && e.tick === w.tick && e.a === m.potatoSlot) pass(w, e.a, e.b);
  }
  if (m.potatoSlot === NO_SLOT) {
    if (w.tick >= m.potatoNextTick && !blasts.has(w) && aliveCount(w) >= 2) assign(w);
    return;
  }
  m.potatoFuse--;
  m.potatoIntensity = potatoIntensity(w.tick - m.potatoNextTick, m.potatoFuseMax);
  // A carrier already falling off the arena takes the bomb down with it (it is lost at the KO);
  // one saved on the way down (a plunger) goes off as soon as it is back.
  if (m.potatoFuse <= 0 && w.beans[m.potatoSlot].state !== BS.FALLING) explode(w);
}

function onTick(w: World, clock: RoundClock, predict: boolean): void {
  if (!predict && !w.over && w.tick >= 0) potatoTick(w);
  royaleTick(w, clock, predict);
}

export const HOT_POTATO: ModeRules = {
  ...ROYALE,
  id: ModeId.HOT_POTATO,
  name: 'Hot Potato',
  minBeans: 3,
  init(w) {
    ROYALE.init(w);
    const m = w.mode;
    m.potatoSlot = NO_SLOT;
    m.potatoNextTick = P.firstTick;
    m.potatoPasser = NO_SLOT;
    m.potatoIntensity = 0;
  },
  onTick,
  onKO(w, victim) {
    const m = w.mode;
    // A carrier that falls off takes the bomb down with it; the next one comes 120 ticks later.
    if (victim === m.potatoSlot) {
      w.beans[victim].hasPotato = false;
      m.potatoSlot = NO_SLOT;
      m.potatoFuse = 0;
      m.potatoIntensity = 0;
      m.potatoNextTick = w.tick + P.nextTicks;
    }
    const pending = blasts.get(w);
    if (pending && pending.slot === victim) blasts.delete(w);
  },
  onHit(w, attacker, victim, cause) {
    if (cause === Cause.SHOVE && attacker !== NO_SLOT && attacker === w.mode.potatoSlot) pass(w, attacker, victim);
  },
  onTouch(w, a, b) {
    const carrier = w.mode.potatoSlot;
    if (carrier === a) pass(w, a, b);
    else if (carrier === b) pass(w, b, a);
  },
  speedMult(w, slot) {
    return slot === w.mode.potatoSlot ? P.carrierSpeed : 1;
  },
  itemsBlocked(w, slot) {
    // A blasted carrier is going down whatever it holds (no plunger or balloon save).
    return blasts.get(w)?.slot === slot;
  },
  results: oneLifeResults,
};
